import React, { useState, useEffect } from 'react';
import axiosClient from '../../api/axiosClient';
import { useToast } from '../../context/ToastContext';

const ProductList = () => {
  const toast = useToast();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const [showModal, setShowModal] = useState(false);
  const [editId, setEditId] = useState(null);
  const [formData, setFormData] = useState({
    productName: '',
    productCode: 0,
    pRate: 0,
    sRate: 0,
    unit: 'KG'
  });
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const res = await axiosClient.get('/products');
      if (res.success) {
        setProducts(res.data || []);
      }
    } catch (err) {
      console.error('Error fetching fish products:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProducts();
  }, []);

  const filteredProducts = products.filter((p) => {
    const term = (search || '').toLowerCase().trim();
    if (!term) return true;
    return (
      (p.productName || '').toLowerCase().includes(term) ||
      String(p.productCode || '').toLowerCase().includes(term) ||
      (p.unit || '').toLowerCase().includes(term)
    );
  });

  const handleOpenAdd = () => {
    setEditId(null);
    setFormData({
      productName: '',
      productCode: 0,
      pRate: 0,
      sRate: 0,
      unit: 'KG'
    });
    setFormError('');
    setShowModal(true);
  };

  const handleOpenEdit = (p) => {
    setEditId(p.Id);
    setFormData({
      productName: p.productName || '',
      productCode: p.productCode || 0,
      pRate: p.PRate || 0,
      sRate: p.SRate || 0,
      unit: p.unit || 'KG'
    });
    setFormError('');
    setShowModal(true);
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setFormError('');
    setSaving(true);
    try {
      if (editId) {
        await axiosClient.put(`/products/${editId}`, formData);
        toast.showSuccess(`Product "${formData.productName}" updated successfully.`);
      } else {
        await axiosClient.post('/products', formData);
        toast.showSuccess(`Product "${formData.productName}" created successfully.`);
      }
      setShowModal(false);
      fetchProducts();
    } catch (err) {
      const errMsg = err.message || 'Error saving product.';
      setFormError(errMsg);
      toast.showError(errMsg);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id, name) => {
    if (window.confirm(`Are you sure you want to delete product "${name}"?`)) {
      try {
        await axiosClient.delete(`/products/${id}`);
        toast.showSuccess(`Product "${name}" deleted successfully.`);
        fetchProducts();
      } catch (err) {
        toast.showError(err.message || 'Error deleting product.');
      }
    }
  };

  return (
    <div className="container-fluid p-0">
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
        <div>
          <h3 className="fw-bold mb-0 text-dark">
            <i className="bi bi-boxes text-primary me-2"></i> Product Master
          </h3>
          <p className="text-muted small mb-0">Manage fish products, standard rates, and units</p>
        </div>
        <button className="btn btn-ocean d-flex align-items-center gap-2" onClick={handleOpenAdd}>
          <i className="bi bi-plus-circle"></i> Add New Product
        </button>
      </div>

      {/* Search Bar */}
      <div className="card shadow-sm border-0 mb-4">
        <div className="card-body p-3">
          <div className="row g-2 align-items-center">
            <div className="col-12 col-md-9">
              <div className="input-group">
                <span className="input-group-text bg-light"><i className="bi bi-search text-primary"></i></span>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search by product name (e.g. Surmai, Pomfret, Rawas, Prawns)..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  autoFocus
                />
                {search && (
                  <button
                    className="btn btn-outline-secondary"
                    type="button"
                    onClick={() => setSearch('')}
                    title="Clear search"
                  >
                    <i className="bi bi-x-lg"></i>
                  </button>
                )}
              </div>
            </div>
            <div className="col-12 col-md-3 text-md-end">
              <span className="badge bg-light text-dark border p-2 w-100 w-md-auto">
                Showing: {filteredProducts.length} of {products.length} Products
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Products Table */}
      <div className="card shadow-sm border-0">
        <div className="card-body p-0">
          <div className="table-responsive">
            <table className="table table-hover align-middle mb-0">
              <thead className="table-light">
                <tr>
                  <th style={{ width: '60px' }}>#</th>
                  <th>Product Name</th>
                  <th className="text-center">Unit</th>
                  <th className="text-end">Std Purchase Rate (₹)</th>
                  <th className="text-end">Std Sale Rate (₹)</th>
                  <th className="text-center" style={{ width: '150px' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="6" className="text-center py-5">
                      <div className="spinner-border text-primary" role="status"></div>
                    </td>
                  </tr>
                ) : filteredProducts.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="text-center py-5 text-muted">
                      <i className="bi bi-inbox fs-3 d-block text-secondary mb-2"></i>
                      No products match "{search}".
                    </td>
                  </tr>
                ) : (
                  filteredProducts.map((p, idx) => (
                    <tr key={p.Id}>
                      <td className="text-muted small">{idx + 1}</td>
                      <td>
                        <div className="d-flex align-items-center gap-2">
                          <span className="badge bg-primary bg-opacity-10 text-primary p-2 rounded-circle">
                            <i className="bi bi-water"></i>
                          </span>
                          <strong className="text-dark fs-6">{p.productName}</strong>
                        </div>
                      </td>
                      <td className="text-center">
                        <span className="badge bg-light text-dark border fw-semibold">{p.unit || 'KG'}</span>
                      </td>
                      <td className="text-end text-muted fw-semibold">
                        ₹{parseFloat(p.PRate || 0).toFixed(2)}
                      </td>
                      <td className="text-end fw-bold text-success">
                        ₹{parseFloat(p.SRate || 0).toFixed(2)}
                      </td>
                      <td className="text-center">
                        <div className="btn-group btn-group-sm">
                          <button
                            className="btn btn-outline-primary"
                            onClick={() => handleOpenEdit(p)}
                            title="Edit"
                          >
                            <i className="bi bi-pencil"></i>
                          </button>
                          <button
                            className="btn btn-outline-danger"
                            onClick={() => handleDelete(p.Id, p.productName)}
                            title="Delete"
                          >
                            <i className="bi bi-trash"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Add / Edit Modal */}
      {showModal && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header bg-dark text-white">
                <h5 className="modal-title">
                  <i className="bi bi-boxes me-2"></i>
                  {editId ? 'Edit Product' : 'Add New Product'}
                </h5>
                <button type="button" className="btn-close btn-close-white" onClick={() => setShowModal(false)}></button>
              </div>

              <form onSubmit={handleFormSubmit}>
                <div className="modal-body p-4">
                  {formError && <div className="alert alert-danger py-2 small">{formError}</div>}

                  <div className="mb-3">
                    <label className="form-label fw-semibold small">Product Name *</label>
                    <input
                      type="text"
                      className="form-control"
                      required
                      placeholder="e.g. SURMAI-I (King Fish Big)"
                      value={formData.productName}
                      onChange={(e) => setFormData({ ...formData, productName: e.target.value })}
                    />
                  </div>

                  <div className="row g-2 mb-3">
                    <div className="col-6">
                      <label className="form-label fw-semibold small">Product Code</label>
                      <input
                        type="number"
                        className="form-control"
                        value={formData.productCode}
                        onChange={(e) => setFormData({ ...formData, productCode: e.target.value })}
                      />
                    </div>
                    <div className="col-6">
                      <label className="form-label fw-semibold small">Unit</label>
                      <select
                        className="form-select"
                        value={formData.unit}
                        onChange={(e) => setFormData({ ...formData, unit: e.target.value })}
                      >
                        <option value="KG">KG (Kilograms)</option>
                        <option value="BOX">BOX (Crates)</option>
                        <option value="PCS">PCS (Pieces)</option>
                        <option value="TON">TON</option>
                      </select>
                    </div>
                  </div>

                  <div className="row g-2">
                    <div className="col-6">
                      <label className="form-label fw-semibold small">Purchase Rate (₹)</label>
                      <input
                        type="number"
                        step="0.01"
                        className="form-control"
                        value={formData.pRate}
                        onChange={(e) => setFormData({ ...formData, pRate: e.target.value })}
                      />
                    </div>
                    <div className="col-6">
                      <label className="form-label fw-semibold small">Sale Rate (₹)</label>
                      <input
                        type="number"
                        step="0.01"
                        className="form-control"
                        value={formData.sRate}
                        onChange={(e) => setFormData({ ...formData, sRate: e.target.value })}
                      />
                    </div>
                  </div>
                </div>

                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary" disabled={saving}>
                    {saving ? 'Saving...' : 'Save Product'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductList;
